<script setup lang="ts">
import { WarningTriangleIcon } from "@voltalis/ui-kit/icons";
import { storeToRefs } from "pinia";
import { computed } from "vue";

import { EInsightsCards, useInsightsMonoController } from "@/app/data/dashboard/cards/mono/insights-mono.controller";
import { EDashboardType } from "@/app/data/dashboard/dashboard.types";
import { useFeatureFlag } from "@/composables/feature-flag/use-feature-flag";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { EDataUnit } from "@/constants/data-units.type";
import { TMonositeMissingInfos } from "@/data/api/kili/consumption/types/monosite-consumption.type";
import { MonositeConsumption } from "@/domains/v4b/site-consumption/monosite-consumption.types";
import { keyOf, useTypedIntl } from "@/locales/typed-intl";
import { ERouteName } from "@/router/router.type";
import { newSectionManagementFeatureFlag } from "@/services/feature-flags/env-feature-flags";
import { useDashboardStore } from "@/stores/dashboard/dashboard-store";
import { getMaybeTruncatedNumber } from "@/utils/number";
import { formatWhValue } from "@/utils/translation-utils";

import ConsumptionEvolutionCard from "@/app/data/dashboard/cards/common/consumption-evolution-card.vue";
import InsightsCard from "@/components/data/dashboard/insights-card.vue";
import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

interface IProps {
  isSiteConsumptionPending?: boolean;
  siteConsumption?: MonositeConsumption;
  missingInfos?: TMonositeMissingInfos;
  siteId?: number;
  isSectionScoped?: boolean;
}

interface IEmits {
  (e: "open-missing-subscription-dialog"): void;
}

const props = defineProps<IProps>();
const emits = defineEmits<IEmits>();

const MULTIPLE = 2;

const { t, k } = useTypedIntl();
const { isEnvFeatureEnabled } = useFeatureFlag();

const dashboardStore = useDashboardStore();
const { insights } = storeToRefs(dashboardStore);

const computedSiteConsumption = computed<MonositeConsumption | undefined>(() => props.siteConsumption);
const computedSiteMissingInfos = computed<TMonositeMissingInfos | undefined>(() => props.missingInfos);

const {
  insightsCardInfosVisible,
  devicesConsumptionPercent,
  devicesConsmptionCost,
  averageConsumptionCostPerM2,
  handleShowDsoConsumptionInfos,
  isMissingDsoSubscriptionWarningVisible,
} = useInsightsMonoController(computedSiteConsumption, computedSiteMissingInfos);
</script>

<template>
  <div class="insights bg-white rounded-md flex flex-col gap-6 p-6">
    <slot name="header" />

    <div class="w-full flex gap-2 flex-wrap">
      <InsightsCard
        v-if="!props.isSectionScoped"
        v-model:infos-visible="insights.isDsoConsumptionInfosVisible"
        class="card"
        :content="formatWhValue(props.siteConsumption?.global.value)"
        :data-test-id="DATA_TEST_IDS.views.dashboard.insights.totalDsoConsumptionCard"
        :infos="t(k.data.dashboard.insights.totalConsumption.tooltip[EDashboardType.MONOSITE])"
        :is-loading="props.isSiteConsumptionPending"
        :title="t(k.data.dashboard.insights.totalConsumption.title[EDashboardType.MONOSITE])"
      >
        <template #bottom>
          <HospitalityButton
            v-if="isMissingDsoSubscriptionWarningVisible"
            class="!text-orange-500"
            :data-test-id="DATA_TEST_IDS.views.dashboard.missingInfos.missingDsoSubscription.button"
            :icon="WarningTriangleIcon"
            severity="link"
            size="xs"
            @click="emits('open-missing-subscription-dialog')"
          >
            {{ t(k.data.dashboard.insights.totalConsumption.missingDsoSubscription) }}
          </HospitalityButton>
        </template>
      </InsightsCard>
      <InsightsCard
        v-model:infos-visible="insights.isDevicesTotalConsumptionInfosVisible"
        class="card"
        :content="formatWhValue(props.siteConsumption?.totalDevices.value)"
        :data-test-id="DATA_TEST_IDS.views.dashboard.insights.totalDevicesConsumptionCard"
        :infos="t(k.data.dashboard.insights.equipmentConsumption.tooltip[EDashboardType.MONOSITE])"
        :is-loading="props.isSiteConsumptionPending"
        :title="t(k.data.dashboard.insights.equipmentConsumption.title)"
      >
        <template #bottom>
          <span class="text-text-secondary flex items-center gap-2">
            <template v-if="devicesConsumptionPercent">
              {{ t(k.data.dashboard.insights.equipmentConsumption.subtitle({ percent: devicesConsumptionPercent })) }}
            </template>
            <template v-else>—</template>
          </span>
        </template>
      </InsightsCard>
      <InsightsCard
        v-if="!props.isSectionScoped"
        v-model:infos-visible="insightsCardInfosVisible[EInsightsCards.ESTIMATED_CONSUMPTION_COST]"
        class="card"
        :content="
          t(
            k.global.units[EDataUnit.EURO]({
              value: getMaybeTruncatedNumber(props.siteConsumption?.globalCost.value) ?? '—',
            }),
          )
        "
        :data-test-id="DATA_TEST_IDS.views.dashboard.insights.estimatedConsumptionCostCard"
        :is-loading="props.isSiteConsumptionPending"
        :title="
          t(k.data.dashboard.insights.totalCost.title[props.siteConsumption?.mainConsumptionSource ?? 'fallback'])
        "
      >
        <template #infos>
          <i18n-t
            class="whitespace-pre-line"
            :keypath="keyOf(k.data.dashboard.insights.totalCost.tooltip)"
            tag="p"
          >
            <template #sitesConsumption>
              <HospitalityButton
                severity="link"
                size="xs"
                @click="handleShowDsoConsumptionInfos(EInsightsCards.ESTIMATED_CONSUMPTION_COST)"
                >{{ t(k.data.dashboard.insights.totalConsumption.title[EDashboardType.MONOSITE]) }}
              </HospitalityButton>
            </template>

            <template #sitesManagementTab>
              <router-link
                :data-test-id="DATA_TEST_IDS.views.dashboard.links.sitesManagementLink"
                target="_blank"
                :to="{
                  name: isEnvFeatureEnabled(newSectionManagementFeatureFlag)
                    ? ERouteName.SITE_SECTIONS_TREE
                    : ERouteName.SITE_SECTIONS,
                  params: { siteId: props.siteId },
                }"
              >
                <HospitalityButton
                  severity="link"
                  size="xs"
                >
                  {{ t(k.components.layout.sideMenu.sitesManagement({ count: MULTIPLE })) }}
                </HospitalityButton>
              </router-link>
            </template>
          </i18n-t>
        </template>

        <template #bottom>
          <span class="text-text-secondary flex items-center gap-2">
            <template v-if="devicesConsmptionCost">
              {{ t(k.data.dashboard.insights.totalCost.subtitle({ amount: devicesConsmptionCost })) }}
            </template>
            <template v-else>—</template>
          </span>
        </template>
      </InsightsCard>

      <InsightsCard
        v-model:infos-visible="insightsCardInfosVisible[EInsightsCards.DSO_PER_AREA_CONSUMPTION]"
        class="card"
        :content="formatWhValue(props.siteConsumption?.totalPerArea?.value)"
        :data-test-id="DATA_TEST_IDS.views.dashboard.insights.avgConsumptionPerM2Card"
        :is-loading="props.isSiteConsumptionPending"
        :title="t(k.data.dashboard.insights.m2Consumption.title)"
      >
        <template #infos>
          <i18n-t
            class="whitespace-pre-line"
            :keypath="
              keyOf(
                k.data.dashboard.insights.m2Consumption.tooltip[
                  props.siteConsumption?.mainConsumptionSource ?? 'fallback'
                ],
              )
            "
            tag="p"
          >
            <template #sitesConsumption>
              <HospitalityButton
                :data-test-id="DATA_TEST_IDS.views.dashboard.links.totalDsoConsumptionLink"
                severity="link"
                size="xs"
                @click="handleShowDsoConsumptionInfos(EInsightsCards.DSO_PER_AREA_CONSUMPTION)"
                >{{ t(k.data.dashboard.insights.totalConsumption.title[EDashboardType.MONOSITE]) }}
              </HospitalityButton>
            </template>

            <template #sitesManagementTab>
              <router-link
                :data-test-id="DATA_TEST_IDS.views.dashboard.links.sitesManagementLink"
                target="_blank"
                :to="{
                  name: isEnvFeatureEnabled(newSectionManagementFeatureFlag)
                    ? ERouteName.SITE_SECTIONS_TREE
                    : ERouteName.SITE_SECTIONS,
                  params: { siteId: props.siteId },
                }"
              >
                <HospitalityButton
                  severity="link"
                  size="xs"
                >
                  {{ t(k.components.layout.sideMenu.sitesManagement({ count: MULTIPLE })) }}
                </HospitalityButton>
              </router-link>
            </template>
          </i18n-t>
        </template>

        <template #bottom>
          <router-link
            v-if="props.missingInfos?.missingSiteArea"
            :data-test-id="DATA_TEST_IDS.views.dashboard.missingInfos.mono.missingSurfaceAreaButton"
            target="_blank"
            :to="{
              name: isEnvFeatureEnabled(newSectionManagementFeatureFlag)
                ? ERouteName.SITE_SECTIONS_TREE
                : ERouteName.SITE_SECTIONS,
              params: { siteId: props.siteId },
            }"
          >
            <HospitalityButton
              class="!text-orange-500"
              :icon="WarningTriangleIcon"
              severity="link"
              size="xs"
            >
              {{ t(k.data.dashboard.insights.m2Consumption.missingSurfaceArea({ count: 1 })) }}
            </HospitalityButton>
          </router-link>
          <span
            v-else
            class="text-text-secondary flex items-center gap-2"
          >
            <template v-if="averageConsumptionCostPerM2">
              {{ t(k.data.dashboard.insights.m2Consumption.subtitle({ amount: averageConsumptionCostPerM2 })) }}
            </template>
            <template v-else>—</template>
          </span>
        </template>
      </InsightsCard>
      <ConsumptionEvolutionCard
        v-model:infos-visible="insightsCardInfosVisible[EInsightsCards.CONSUMPTION_EVOLUTION]"
        class="card"
        :data-test-id="DATA_TEST_IDS.views.dashboard.insights.consumptionEvolutionCard"
        :is-loading="props.isSiteConsumptionPending"
        :title="
          siteConsumption?.mainConsumptionSource === 'DSO'
            ? t(k.data.dashboard.insights.consumptionEvolution.title.DSO({ count: MULTIPLE }))
            : t(
                k.data.dashboard.insights.consumptionEvolution.title[
                  siteConsumption?.mainConsumptionSource ?? 'fallback'
                ],
              )
        "
        :type="EDashboardType.MONOSITE"
        :value="props.siteConsumption?.totalEvolution.value"
      >
        <template #infos>
          <i18n-t
            class="whitespace-pre-line"
            :keypath="
              keyOf(
                k.data.dashboard.insights.consumptionEvolution.tooltip[
                  props.siteConsumption?.mainConsumptionSource ?? 'fallback'
                ],
              )
            "
            tag="p"
          >
            <template #sitesConsumption>
              <HospitalityButton
                severity="link"
                size="xs"
                :data-test-id="DATA_TEST_IDS.views.dashboard.links.totalDsoConsumptionLink"
                @click="handleShowDsoConsumptionInfos(EInsightsCards.CONSUMPTION_EVOLUTION)"
                >{{ t(k.data.dashboard.insights.totalConsumption.title[EDashboardType.MONOSITE]) }}
              </HospitalityButton>
            </template>
          </i18n-t>
        </template>
      </ConsumptionEvolutionCard>
    </div>
  </div>
</template>

<style lang="scss">
.insights {
  .card {
    @apply flex-1 min-w-[300px];
  }
}
</style>
